import { type RefObject, useCallback, useLayoutEffect, useRef } from "react";

/**
 * Keeps the caret sensible when an input reformats its own value on every
 * keystroke (phone, currency, postal code…). The trick every masking library
 * uses: measure the caret by the number of *significant* characters (e.g.
 * digits) to its left, not by raw string offset — formatting characters shift
 * the offset but not the meaning. After the reformatted value lands in the DOM,
 * put the caret just after that many significant characters.
 *
 * Returns `setCaretTarget(n)` — call it from `onChange` with the number of
 * significant characters that should sit to the left of the caret in the
 * reformatted value. Pair it with `countSignificantBefore` to compute `n`.
 */
export function useFormattedCaret(
  ref: RefObject<HTMLInputElement | null>,
  formattedValue: string,
  isSignificant: (char: string) => boolean,
): (significantBeforeCaret: number) => void {
  const targetRef = useRef<number | undefined>(undefined);

  const setCaretTarget = useCallback((significantBeforeCaret: number) => {
    targetRef.current = significantBeforeCaret;
  }, []);

  useLayoutEffect(() => {
    const el = ref.current;
    const target = targetRef.current;
    if (!el || target === undefined) return;
    targetRef.current = undefined;

    if (target <= 0) {
      el.setSelectionRange(0, 0);
      return;
    }
    let seen = 0;
    let pos = el.value.length;
    for (let i = 0; i < el.value.length; i++) {
      if (isSignificant(el.value[i]!)) {
        seen++;
        if (seen === target) {
          pos = i + 1;
          break;
        }
      }
    }
    el.setSelectionRange(pos, pos);
  }, [formattedValue, ref, isSignificant]);

  return setCaretTarget;
}

/** Count significant characters in `value` before `offset`. */
export function countSignificantBefore(
  value: string,
  offset: number,
  isSignificant: (char: string) => boolean,
): number {
  let count = 0;
  for (let i = 0; i < offset && i < value.length; i++) {
    if (isSignificant(value[i]!)) count++;
  }
  return count;
}
